import React from "react";
import logo from "../../images/logo2.svg";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../../Context/AppContext";

/* ── Sub-components ── */
const ColHeading = ({ children }) => (
  <div className="mb-4">
    <div className="flex items-center gap-2 mb-0.5">
      <span className="w-[3px] h-[14px] bg-[#ff601e] rounded-full flex-shrink-0" />
      <p className="font-poppins font-black text-[11px] text-white uppercase tracking-[.1em]">
        {children}
      </p>
    </div>
  </div>
);

const FLink = ({ to, onClick, children }) => (
  <Link
    to={to || "#"}
    onClick={onClick}
    className="flex items-center gap-2 font-poppins text-[12.5px] text-[#7abcb8] hover:text-white transition-colors duration-150 py-[3.5px] group"
    style={{ textDecoration: "none" }}
  >
    <span className="w-1 h-1 rounded-full bg-[#5fbdb3] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    {children}
  </Link>
);

const ELink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2 font-poppins text-[12.5px] text-[#7abcb8] hover:text-white transition-colors duration-150 py-[3.5px] group"
    style={{ textDecoration: "none" }}
  >
    <span className="w-1 h-1 rounded-full bg-[#5fbdb3] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    {children}
  </a>
);

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-[30px] h-[30px] rounded-[8px] bg-[#162c2a] border border-[#2d6560] flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-black text-[#5fbdb3] uppercase tracking-[.08em] mb-0.5">
        {label}
      </p>
      <p className="text-[12px] text-[#c5e8e6] font-medium leading-snug">
        {value}
      </p>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════ */
const Footer = () => {
  const { contextCategories } = useApp();
  const navigate = useNavigate();

  const handleCategoryClick = (id, name) => {
    navigate("/categories", {
      state: { category_id: id, category_name: name },
    });
  };

  const midIndex = Math.ceil(contextCategories.length / 2);
  const firstHalf = contextCategories.slice(0, midIndex);
  const secondHalf = contextCategories.slice(midIndex);

  return (
    <footer className="bg-[#0f1e1c] w-full font-poppins">
      <div className="w-full h-[3px] bg-gradient-to-r from-[#ff601e] via-[#5fbdb3] to-[#ff601e]" />

      <div className="bg-[#162c2a] px-5 sm:px-8 lg:px-14 pt-12 pb-8">
        <div className="mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[280px_1fr_1fr_1fr_240px] gap-x-10 gap-y-10 items-start">
            {/* ── COL 1: Brand ── */}
            <div className="flex flex-col gap-5 sm:col-span-2 xl:col-span-1">
              {/* Logo pill */}
              <div className="bg-[#1e4a46] border border-[#2d6560] rounded-2xl p-4 flex items-center gap-4 w-full max-w-[260px] sm:max-w-none xl:max-w-none">
                <div>
                  <img
                    src={logo}
                    alt="MediSYS"
                    className="h-[75px] w-auto object-contain"
                  />
                </div>
              </div>

              <p className="text-[12px] text-[#7abcb8] leading-[1.8] max-w-[240px]">
                Your trusted pharmacy partner - delivering health, wellness
                &amp; convenience to your door across Hyderabad.
              </p>

              {/* Trust badges */}
              <div className="flex flex-col gap-2.5">
                {[
                  {
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#5fbdb3"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    ),
                    text: "Licensed Pharmacy · GSTIN: 36ABTFM9976H1Z7",
                  },
                  {
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#5fbdb3"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    ),
                    text: "100% Genuine · Verified Medicines",
                  },
                ].map((b, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2.5 bg-[#1e4a46] border border-[#2d6560] rounded-xl px-3 py-2.5"
                  >
                    <span className="flex-shrink-0">{b.icon}</span>
                    <span className="text-[11px] font-semibold text-[#9fd8d4] leading-snug">
                      {b.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Social icons */}
              <div className="flex items-center gap-2.5">
                {[
                  {
                    href: "https://www.facebook.com/p/Medisys-Pharmacy-61553928654627/",
                    icon: (
                      <svg
                        width="17"
                        height="17"
                        fill="white"
                        viewBox="0 0 24 24"
                      >
                        <path d="M22.675 0H1.325C.593 0 0 .593 0 1.326v21.348C0 23.406.593 24 1.325 24h11.495v-9.294H9.689v-3.622h3.131V8.413c0-3.1 1.894-4.788 4.659-4.788 1.325 0 2.463.1 2.793.145v3.24l-1.918.001c-1.504 0-1.796.714-1.796 1.76v2.307h3.591l-.467 3.622h-3.124V24h6.116c.73 0 1.324-.593 1.324-1.326V1.326C24 .593 23.406 0 22.675 0z" />
                      </svg>
                    ),
                  },
                  {
                    href: "https://www.instagram.com/medisys_pharmacy",
                    icon: (
                      <svg
                        width="17"
                        height="17"
                        fill="white"
                        viewBox="0 0 24 24"
                      >
                        <path d="M7.75 2h8.5C19.554 2 22 4.446 22 7.75v8.5c0 3.304-2.446 5.75-5.75 5.75h-8.5C4.446 22 2 19.554 2 16.25v-8.5C2 4.446 4.446 2 7.75 2zm8.5 2h-8.5A3.75 3.75 0 0 0 4 7.75v8.5A3.75 3.75 0 0 0 7.75 20h8.5A3.75 3.75 0 0 0 20 16.25v-8.5A3.75 3.75 0 0 0 16.25 4zm-4.25 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm5.5-.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                      </svg>
                    ),
                  },
                  // {
                  //   href: "https://twitter.com/",
                  //   icon: (
                  //     <svg
                  //       width="17"
                  //       height="17"
                  //       fill="white"
                  //       viewBox="0 0 24 24"
                  //     >
                  //       <path d="M0.922 0L10.182 13.5 1.118 24h2.008L11.07 14.793 17.385 24h7.588L15.298 9.897 23.835 0h-2.002L14.41 8.6 8.509 0H0.922zM3.32 1.263h4.524L22.574 22.737h-4.525L3.32 1.263z" />
                  //     </svg>
                  //   ),
                  // },
                  {
                    href: "https://www.youtube.com/@MediSYSPharmacy/",
                    icon: (
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
                        <polygon
                          points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"
                          fill="white"
                          stroke="none"
                        />
                      </svg>
                    ),
                  },
                ].map((s, i) => (
                  <a
                    key={i}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-[10px] bg-[#1e4a46] border border-[#2d6560] flex items-center justify-center hover:bg-[#ff601e] hover:border-[#ff601e] transition-all duration-200 hover:-translate-y-0.5"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* ── COL 2: Quick Links ── */}
            <div className="flex flex-col">
              <ColHeading>Quick Links</ColHeading>
              <FLink to="/about-us">About Us</FLink>
              <FLink to="/products">Shop All Products</FLink>
              <FLink to="/my-prescriptions">Upload Prescription</FLink>
              <FLink to="/my-orders">Track My Order</FLink>
              <FLink to="/blogs">Health Blog</FLink>
              {/* <FLink to="/home">Offers &amp; Deals</FLink> */}
              <FLink to="/faq">FAQ</FLink>
              <FLink to="/terms-and-conditions">Terms &amp; Conditions</FLink>
              <FLink to="/privacy-policy">Privacy Policy</FLink>
              <FLink to="/cancellation-policy">Cancellation Policy</FLink>
            </div>

            {/* ── COL 3: Branches + Hours ── */}
            <div className="flex flex-col">
              <ColHeading>Branch Locations</ColHeading>

              <ELink
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent("Panjagutta, Hyderabad 500082")}`}
              >
                Panjagutta, 500082
              </ELink>

              <ELink
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent("Manikonda, Hyderabad 500104")}`}
              >
                Manikonda, 500104
              </ELink>

              <ELink
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent("Bandlaguda, Hyderabad 500091")}`}
              >
                Bandlaguda, 500091
              </ELink>

              <ELink
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent("Chikkadpally, Hyderabad 500044")}`}
              >
                Chikkadapally, 500044
              </ELink>

              <ELink
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent("Inderbagh, Hyderabad 500001")}`}
              >
                Inderbagh, 500001
              </ELink>
              <Link
                to="/medical-stores"
                className="mt-2 flex items-center gap-1 text-[12px] font-bold text-[#5fbdb3] hover:text-white transition-colors"
                style={{ textDecoration: "none" }}
              >
                View All Stores
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 12L10 8L6 4"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>

              {/* Working hours */}
              <div className="mt-6">
                <ColHeading>Working Hours</ColHeading>
                {[
                  "Mon – Sat  ·  8:00 AM – 10:00 PM",
                  "Sunday  ·  9:00 AM – 8:00 PM",
                  "Online Store  ·  24 / 7",
                ].map((h, i) => (
                  <p
                    key={i}
                    className="text-[12px] text-[#7abcb8] py-[3.5px] leading-snug"
                  >
                    {h}
                  </p>
                ))}
              </div>
            </div>

            {/* ── COL 4: Categories (both halves) ── */}
            <div className="flex flex-col">
              <ColHeading>Categories</ColHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                <div className="flex flex-col">
                  {firstHalf.map((item) => (
                    <FLink
                      key={item.category_id}
                      onClick={(e) => {
                        e.preventDefault();
                        handleCategoryClick(
                          item.category_id,
                          item.category_name,
                        );
                      }}
                    >
                      {item.category_name}
                    </FLink>
                  ))}
                </div>
                <div className="flex flex-col">
                  {secondHalf.map((item) => (
                    <FLink
                      key={item.category_id}
                      onClick={(e) => {
                        e.preventDefault();
                        handleCategoryClick(
                          item.category_id,
                          item.category_name,
                        );
                      }}
                    >
                      {item.category_name}
                    </FLink>
                  ))}
                </div>
              </div>
            </div>

            {/* ── COL 5: Contact ── */}
            <div className="flex flex-col sm:col-span-2 xl:col-span-1">
              <ColHeading>Get In Touch</ColHeading>

              {/* Contact info card */}
              <div className="bg-[#1e4a46] border border-[#2d6560] rounded-2xl p-4 flex flex-col gap-4 mb-4">
                <InfoRow
                  label="Phone / WhatsApp"
                  value="+91 97010 00000"
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#5fbdb3"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12 19.79 19.79 0 0 1 1.65 3.4 2 2 0 0 1 3.62 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  }
                />
                <InfoRow
                  label="Email"
                  value="support@medisys.in"
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#5fbdb3"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  }
                />
                <InfoRow
                  label="Head Office"
                  value="Panjagutta, Hyderabad – 500082"
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#5fbdb3"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  }
                />
              </div>

              <Link to="/contact-us" style={{ textDecoration: "none" }}>
                <button className="w-full flex items-center justify-center gap-2.5 bg-[#ff601e] hover:bg-[#e55519] text-white font-bold text-[13px] h-11 rounded-xl transition-all duration-200 shadow-[0_3px_12px_rgba(255,96,30,0.3)] hover:shadow-[0_5px_18px_rgba(255,96,30,0.45)] hover:-translate-y-0.5 border-none cursor-pointer">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12 19.79 19.79 0 0 1 1.65 3.4 2 2 0 0 1 3.62 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  Contact Us
                </button>
              </Link>

              {/* App download hint */}
              <div className="mt-4 flex items-center gap-2 bg-[#1e4a46] border border-[#2d6560] rounded-xl px-3.5 py-3">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5fbdb3"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
                <div>
                  <p className="text-[10px] font-bold text-[#5fbdb3] uppercase tracking-[.08em]">
                    Order via WhatsApp
                  </p>
                  <p className="text-[12px] text-[#9fd8d4] font-medium">
                    +91 97010 00000
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="px-5 sm:px-8 lg:px-14">
        <div className="max-w-[1200px] mx-auto h-px bg-white/[0.07]" />
      </div>

      {/* ── Bottom bar ── */}
      <div className="bg-[#162c2a] px-5 sm:px-8 lg:px-14 py-5">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="text-[12px] text-[#5a8b88]">
            &copy; {new Date().getFullYear()} MediSYS Pharmacy. All rights
            reserved.
          </p>

          <div className="flex items-center gap-1 flex-wrap justify-center">
            {[
              { label: "Terms", to: "/terms-and-conditions" },
              { label: "Privacy", to: "/privacy-policy" },
              { label: "Returns", to: "/cancellation-policy" },
              { label: "Contact", to: "/contact-us" },
            ].map((item, i, arr) => (
              <React.Fragment key={item.to}>
                <Link
                  to={item.to}
                  className="text-[12px] text-[#5a8b88] hover:text-white transition-colors px-2 py-0.5"
                  style={{ textDecoration: "none" }}
                >
                  {item.label}
                </Link>
                {i < arr.length - 1 && (
                  <span className="text-white/15 text-[12px] select-none">
                    |
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>

          <a
            href="https://novagito.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/[0.05] border border-white/[0.08] rounded-xl px-4 py-2 hover:bg-white/[0.1] hover:border-[#5fbdb3]/40 transition-all duration-200 group flex-shrink-0"
            style={{ textDecoration: "none" }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              className="flex-shrink-0"
            >
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="#5fbdb3" />
            </svg>
            <span className="text-[11px] text-[#7abcb8] group-hover:text-white/60 transition-colors font-poppins whitespace-nowrap">
              Powered by{" "}
              <span className="font-bold text-[#3cd916] group-hover:text-[#5fbdb3] transition-colors">
                Novagito AI
              </span>
            </span>
          </a>
        </div>
      </div>

      {/* ── Disclaimer strip ── */}
      <div className="w-full bg-[#0a1715] border-t border-white/[0.05] px-5 sm:px-8 lg:px-14 py-4">
        <p className="text-[11px] text-white/40 leading-[1.75] text-justify italic max-w-[1200px] mx-auto">
          <span className="font-bold text-red-400/70 not-italic">
            Disclaimer:{" "}
          </span>
          MediSYS Pharmacy{" "}
          <span className="font-semibold text-white/30 not-italic">
            (GSTIN: 36ABTFM9976H1Z7)
          </span>{" "}
          is a licensed pharmacy. Prescription medicines are supplied only
          against a valid doctor's prescription, verified by our pharmacists
          before delivery. The product information and content on this site are
          provided for convenience and general awareness and should not replace
          medical advice. Please consult a qualified healthcare professional for
          diagnosis and treatment. Prices, stock availability, and offers are
          subject to change. Final confirmation is provided at the time of order
          processing. Orders may be modified or cancelled if the required
          prescription is not valid or if items are unavailable. By using this
          website, you acknowledge and accept our Terms &amp; Conditions,
          Privacy Policy, and Return Policy.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
